import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { useGetExecutionStats } from '../../../../hooks/API/wizard.api';
import '../../../../styles/wizard.css';

const Step13IotSummary = () => {
  const navigate = useNavigate();
  const { workspace, project, iot } = useOnboarding();
  const [showConfetti, setShowConfetti] = useState(false);

  const projectId = project?.project_id;
  const { data: executionStatsData, isLoading: isStatsLoading } = useGetExecutionStats(projectId);

  useEffect(() => {
    // Show confetti animation
    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleGoToDashboard = () => {
    navigate('/app/dashboard');
  };

  const handleViewReports = () => {
    navigate('/app/reports');
  };

  // Get stats from API response or fallback to context data
  const stats = executionStatsData?.stats || {
    total_test_cases: iot.testCaseIds?.length || 0,
    total_passed: iot.testRun?.results?.passed || 0,
    total_failed: iot.testRun?.results?.failed || 0,
    not_run: (iot.testCaseIds?.length || 0) - ((iot.testRun?.results?.passed || 0) + (iot.testRun?.results?.failed || 0)),
    pass_rate: iot.testRun?.results?.total > 0
      ? `${Math.round((iot.testRun.results.passed / iot.testRun.results.total) * 100)}%`
      : "0%",
  };

  // Calculate pass rate as a number for conditional styling
  const passRateNum = parseInt(stats.pass_rate) || 0;

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={13} completedSteps={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]} />
        
        <WizardCard
          title="IoT Test Automation Setup Complete!"
          subtitle="Your hardware testing environment is ready"
        >
          {/* Success celebration */}
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{
              width: '120px',
              height: '120px',
              margin: '0 auto 2rem',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--success-color) 0%, #45b7d1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '4rem',
              color: 'white',
              boxShadow: '0 8px 20px rgba(76, 201, 240, 0.3)',
              animation: showConfetti ? 'pulse 1s ease-in-out infinite' : 'none'
            }}>
              🎉
            </div>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: '600',
              marginBottom: '1rem',
              background: 'linear-gradient(135deg, var(--primary-color) 0%, var(--success-color) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Congratulations!
            </h2>
            <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)', marginBottom: '3rem' }}>
              Your IoT/Hardware testing automation is set up and ready to use
            </p>
          </div>

          {/* Setup summary */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            marginBottom: '3rem'
          }}>
            {/* Workspace card */}
            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(76, 201, 240, 0.1) 0%, rgba(108, 92, 231, 0.05) 100%)',
              border: '1px solid rgba(76, 201, 240, 0.2)',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📁</div>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                WORKSPACE
              </h4>
              <p style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                {workspace?.name || 'My Workspace'}
              </p>
            </div>

            {/* Project card */}
            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(76, 201, 240, 0.1) 0%, rgba(108, 92, 231, 0.05) 100%)',
              border: '1px solid rgba(76, 201, 240, 0.2)',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🔧</div>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                PROJECT
              </h4>
              <p style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                {project?.name || 'IoT Project'}
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                {iot.device?.category || 'Hardware Device'}
              </p>
            </div>

            {/* Device card */}
            <div style={{
              padding: '1.5rem',
              background: 'linear-gradient(135deg, rgba(76, 201, 240, 0.1) 0%, rgba(108, 92, 231, 0.05) 100%)',
              border: '1px solid rgba(76, 201, 240, 0.2)',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📡</div>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                DEVICE
              </h4>
              <p style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                {iot.device?.deviceId?.substring(0, 16) || 'Device'}...
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--success-color)' }}>
                ● Connected
              </p>
            </div>
          </div>

          {/* Test results summary */}
          <div style={{
            padding: '2rem',
            background: 'var(--bg-gray-50)',
            borderRadius: '12px',
            marginBottom: '3rem'
          }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', textAlign: 'center' }}>
              Initial Test Run Results
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 0.75rem',
                  borderRadius: '50%',
                  background: passRateNum >= 80 ? 'var(--success-bg)' : 'rgba(247, 37, 133, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  fontWeight: '600',
                  color: passRateNum >= 80 ? 'var(--success-color)' : 'var(--warning-color)'
                }}>
                  {stats.pass_rate}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Pass Rate</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 0.75rem',
                  borderRadius: '50%',
                  background: 'rgba(76, 201, 240, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  fontWeight: '600',
                  color: 'var(--primary-color)'
                }}>
                  {stats.total_passed}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Tests Passed</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 0.75rem',
                  borderRadius: '50%',
                  background: 'rgba(108, 92, 231, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  fontWeight: '600',
                  color: '#6c5ce7'
                }}>
                  {stats.total_test_cases}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Test Cases</p>
              </div>
            </div>

            {/* Evidence captured */}
            {/* {(iot.evidence?.captureLogs || iot.evidence?.captureTelemetry || 
              iot.evidence?.captureVideo || iot.evidence?.captureSnapshots) && (
              <div style={{
                padding: '1rem',
                background: 'white',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                  Evidence Captured
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {iot.evidence.captureLogs && (
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      background: 'var(--success-bg)',
                      color: 'var(--success-color)',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      📝 Logs
                    </span>
                  )}
                  {iot.evidence.captureTelemetry && (
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      background: 'var(--success-bg)',
                      color: 'var(--success-color)',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      📊 Telemetry
                    </span>
                  )}
                  {iot.evidence.captureVideo && (
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      background: 'var(--success-bg)',
                      color: 'var(--success-color)',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      🎥 Video
                    </span>
                  )}
                  {iot.evidence.captureSnapshots && (
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      background: 'var(--success-bg)',
                      color: 'var(--success-color)',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      📸 Snapshots
                    </span>
                  )}
                </div>
              </div>
            )} */}
          </div>

          {/* Next steps */}
          <div style={{
            padding: '2rem',
            background: 'linear-gradient(135deg, rgba(76, 201, 240, 0.05) 0%, rgba(108, 92, 231, 0.05) 100%)',
            border: '1px solid rgba(76, 201, 240, 0.2)',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              What's Next?
            </h3>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'var(--primary-color)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  flexShrink: 0
                }}>
                  1
                </div>
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                    Explore your Dashboard
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    View test results, analytics, and trends in real-time
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'var(--primary-color)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  flexShrink: 0
                }}>
                  2
                </div>
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                    Create more test cases
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Expand your hardware test coverage with AI or recording
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: 'var(--primary-color)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  flexShrink: 0
                }}>
                  3
                </div>
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                    Set up CI/CD integration
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Automate testing in your deployment pipeline
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={handleGoToDashboard}
              className="wizard-btn wizard-btn-primary"
              style={{ minWidth: '200px' }}
            >
              📊 Go to Dashboard
            </button>
            <button
              onClick={handleViewReports}
              className="wizard-btn wizard-btn-secondary"
              style={{ minWidth: '200px' }}
            >
              📋 View Reports
            </button>
          </div>
        </WizardCard>
      </WizardContainer>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        ${showConfetti ? `
          @keyframes confetti-fall {
            0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
          }
        ` : ''}
      `}</style>
    </PrivateWizardLayout>
  );
};

export default Step13IotSummary;